import React, { useState } from 'react';

const MedicineProducts = ({ onInquireProduct }) => {
  const [activeTab, setActiveTab] = useState('All');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageStatus, setImageStatus] = useState({});

  const handleImageError = (id) => {
    setImageStatus(prev => ({
      ...prev,
      [id]: 'error'
    }));
  };

  const medicines = [
    {
      id: 1,
      name: 'VAR10 Plus Tablets',
      category: 'Tablets',
      description: 'A comprehensive nutritional supplement containing L-Carnitine, Omega-3 fatty acids, Co-Enzyme Q10, vitamins and antioxidants to support overall maternal wellness.',
      dosage: 'As directed by the physician',
      price: '₹---',
      image: '/medicine/3.jpeg',
      benefits: [
        'Supports overall maternal health',
        'Provides essential fatty acids and antioxidants',
        'Helps improve energy and metabolism',
        'Supports healthy pregnancy nutrition'
      ],
      composition: [
        'L-Carnitine',
        'L-Tartrate',
        'Flaxseed (Omega-3)',
        'Co-Enzyme Q10',
        'L-Arginine',
        'Zinc Sulphate',
        'Wheat Germ Oil',
        'Mixed Carotenoids',
        'Astaxanthin',
        'Piperine',
        'Lycopene'
      ]
    },
    {
      id: 2,
      name: 'V-GUT-PLUS Suspension',
      category: 'Suspensions',
      description: 'A multi-action suspension formulated with sodium alginate, sodium bicarbonate, and calcium carbonate to provide quick relief from acidity, heartburn, and indigestion.',
      dosage: 'As directed by the physician',
      price: '₹---',
      image: '/medicine/v-gut.png',
      benefits: [
        'Quick relief from acidity and heartburn',
        'Forms a protective layer to reduce acid reflux',
        'Effective for indigestion and gastric discomfort',
        'Safe for pregnant and breastfeeding women (as per label)'
      ],
      composition: [
        'Sodium Alginate',
        'Sodium Bicarbonate',
        'Calcium Carbonate'
      ]
    },
    {
      id: 3,
      name: 'Varcof-DX Syrup',
      category: 'Syrups',
      description: 'An advanced cough relief formula combining Dextromethorphan, Chlorpheniramine, and Phenylephrine to relieve cough, cold, nasal congestion, and throat irritation.',
      dosage: 'As directed by the physician',
      price: '₹---',
      image: '/medicine/v-cough.png',
      benefits: [
        'Relieves dry and irritating cough',
        'Reduces nasal congestion',
        'Provides relief from allergic symptoms like sneezing and runny nose',
        'Sugar-free formula suitable for diabetic patients'
      ],
      composition: [
        'Dextromethorphan HBr',
        'Chlorpheniramine Maleate',
        'Phenylephrine HCl'
      ]
    }
  ];

  const categories = ['All', 'Tablets', 'Syrups', 'Suspensions'];

  const filteredMedicines = activeTab === 'All' 
    ? medicines 
    : medicines.filter(med => med.category === activeTab);

  return (
    <section id="products" className="py-24 bg-slate-900/50 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-amber-500/10 text-amber-400 border border-amber-500/20 mb-4">
            Product Portfolio
          </span>
          <h2 className="heading-premium text-3xl md:text-5xl font-black text-white mb-6">
            Our Premium <span className="text-accent-gold">Formulations</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Discover our high-quality pharmaceutical products developed with stringent quality standards for patient care.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center mb-12">
          <div className="bg-slate-800/60 p-1.5 rounded-2xl inline-flex space-x-1 border border-slate-700/50 shadow-sm max-w-full overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold tracking-wide transition-all duration-300 whitespace-nowrap ${
                  activeTab === cat
                    ? 'bg-emerald-500/20 text-emerald-400 shadow-md border border-emerald-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredMedicines.map((medicine) => (
            <div 
              key={medicine.id} 
              className="bg-slate-800/60 rounded-3xl border border-slate-700/50 shadow-sm hover:shadow-emerald-500/10 hover:-translate-y-2 transition-all duration-300 overflow-hidden flex flex-col group relative"
            >
              <div className="h-64 bg-slate-900/50 p-6 flex items-center justify-center border-b border-slate-700/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {imageStatus[medicine.id] === 'error' ? (
                  <div className="w-full h-full flex items-center justify-center bg-slate-900">
                    <span className="text-slate-500 text-sm font-medium">Image not available</span>
                  </div>
                ) : (
                  <img 
                    src={medicine.image} 
                    alt={medicine.name}
                    className="h-full w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                    onError={() => handleImageError(medicine.id)}
                    onLoad={() => setImageStatus(prev => ({ ...prev, [medicine.id]: 'loaded' }))}
                  />
                )}
                <span className="absolute top-4 right-4 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                  {medicine.category}
                </span>
              </div>
              
              <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-emerald-400 transition-colors duration-200">{medicine.name}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6 flex-grow line-clamp-3">
                  {medicine.description}
                </p>
                
                <button
                  onClick={() => setSelectedProduct(medicine)}
                  className="w-full py-3 rounded-xl border border-slate-600 hover:border-emerald-500 text-slate-300 hover:text-white hover:bg-emerald-500 font-semibold text-sm transition-all duration-300 shadow-sm"
                >
                  Explore Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Medicine Modal */}
        {selectedProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Modal Backdrop */}
            <div 
              className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm transition-opacity duration-300"
              onClick={() => setSelectedProduct(null)}
            />
            
            {/* Modal Content */}
            <div className="relative bg-slate-800 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-700/50 flex flex-col animate-fade-in-up z-10">
              
              {/* Close Button */}
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-5 right-5 z-20 p-2.5 rounded-full bg-slate-700 border border-slate-600/50 text-slate-300 hover:text-white hover:bg-slate-600 transition-all duration-200"
                aria-label="Close details"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {/* Left side: Image */}
                <div className="p-8 bg-slate-900/50 flex items-center justify-center border-b md:border-b-0 md:border-r border-slate-700/50 min-h-[300px]">
                  {imageStatus[selectedProduct.id] === 'error' ? (
                    <span className="text-slate-500 text-sm font-medium">Image not available</span>
                  ) : (
                    <img 
                      src={selectedProduct.image} 
                      alt={selectedProduct.name}
                      className="max-h-[250px] w-auto object-contain"
                    />
                  )}
                </div>

                {/* Right side: Product Data */}
                <div className="p-8 flex flex-col justify-between">
                  <div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 uppercase tracking-wider mb-4">
                      {selectedProduct.category}
                    </span>
                    <h3 className="text-2xl font-extrabold text-white mb-4">{selectedProduct.name}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-6">
                      {selectedProduct.description}
                    </p>

                    {/* Composition */}
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Key Ingredients</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedProduct.composition.map((item, idx) => (
                          <span 
                            key={idx} 
                            className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-700/60 text-slate-300 border border-slate-600/50"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Benefits */}
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Key Benefits</h4>
                      <ul className="space-y-2">
                        {selectedProduct.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start">
                            <span className="flex-shrink-0 w-5 h-5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-md flex items-center justify-center mr-2.5 mt-0.5">
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                            <span className="text-xs sm:text-sm text-slate-400">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Dosage */}
                    <div className="bg-amber-500/5 border border-amber-500/10 rounded-2xl p-4 flex items-start space-x-3 mb-6">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider">Dosage Warning</h4>
                        <p className="text-xs text-amber-300 mt-0.5">{selectedProduct.dosage}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setSelectedProduct(null);
                        if (onInquireProduct) {
                          onInquireProduct(selectedProduct.name);
                        }
                      }}
                      className="flex-grow py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold text-sm text-center shadow-md shadow-emerald-500/10 hover:shadow-lg transition-all duration-200"
                    >
                      Inquire About Product
                    </button>
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="py-3 px-6 border border-slate-600 hover:bg-slate-700 text-slate-300 rounded-xl font-bold text-sm text-center transition-all duration-200"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Bottom Contact Inquire Link */}
        <div className="mt-16 text-center">
          <p className="text-slate-400 text-sm">
            Looking for custom medical formulations or Bulk distribution?{' '}
            <a 
              href="#contact" 
              onClick={(e) => {
                e.preventDefault();
                onInquireProduct('Custom Formulation / Bulk Distribution');
              }}
              className="text-emerald-400 font-bold hover:text-emerald-300 transition-colors duration-200 border-b border-emerald-500/30 pb-0.5 hover:border-emerald-400"
            >
              Contact our product team
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default MedicineProducts;
